import { Button, Flex, Grid, Loader, ScrollView } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import {
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import Tippy from "@tippyjs/react";
import AlertIcon from "@mui/icons-material/NotificationImportant";

import {
  alertLogsByStatus,
  devicesByBranchID,
  devicesByCompanyID,
  deviceVariablesByDeviceID,
} from "graphql/queries";
import { globalStrings } from "config/translations/es";
import MonitorMap from "components/monitor/MonitorMap";
import { CompanyCreateForm } from "modules/companies";
import { BranchCreateForm } from "modules/branches";
import { DeviceCreateForm } from "modules/devices";
import usePermissions from "hooks/usePermission";
import { configInitialValues } from "config/constants";
import DeviceChart from "components/devices/DeviceChart";
import ChartFiltersForm from "components/monitor/ChartFiltersForm";
import { LayoutContext } from "layout/context/LayoutContext";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";

const AlertTreeIcon = ({ item }) => (
  <Tippy
    content={
      <div>
        <p>
          {item.name} / {globalStrings.severity.options[item.severity]}
        </p>
        <p style={{ marginBottom: 0 }}>{item.description}</p>
      </div>
    }
  >
    <AlertIcon style={{ fontSize: "20px" }} htmlColor={item.severityColor} />
  </Tippy>
);

const Monitor = () => {
  const { me } = useContext(LayoutContext);
  const [canCreateCompany, canCreateBranch, canCreateDevice] = usePermissions([
    "company:create",
    "company-branch:create",
    "device:create",
  ]);
  const [alerts, setAlerts] = useState([]);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [deviceVariables, setDeviceVariables] = useState({
    loading: false,
    data: [],
  });
  const [modalCompanyVisible, setModalCompanyVisible] = useState(false);
  const [modalBranchVisible, setModalBranchVisible] = useState(false);
  const [modalDeviceVisible, setModalDeviceVisible] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState();
  const [refreshFilter, setRefreshFilter] = useState(false);
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const devicesMapped = useMemo(
    () =>
      devices?.map((device) => {
        const alertsFiltered =
          alerts?.filter((alert) => alert.deviceID === device.id) ?? [];
        return {
          ...device,
          alerts: alertsFiltered,
        };
      }),
    [devices, alerts]
  );
  const isAutoRefreshAll = useMemo(
    () =>
      deviceVariables.data?.length > 0 &&
      deviceVariables.data?.every((devVar) => !!devVar.autoRefresh),
    [deviceVariables.data]
  );

  const onUpdateCharts = (values) => {
    setDeviceVariables((prevSt) => ({
      ...prevSt,
      data: prevSt.data?.map((prevItem) => ({
        ...prevItem,
        period: values.period,
        dateFrom: values.dateFrom,
        dateTo: values.dateTo,
        scheduleType: values.scheduleType,
        chartColor: values.chartColor,
      })),
    }));
  };

  const onAutoRefreshAll = (value) => {
    setDeviceVariables((prevSt) => ({
      ...prevSt,
      data: prevSt.data?.map((prevItem) => ({
        ...prevItem,
        autoRefresh: value,
      })),
    }));
  };

  const onAddedCompany = () => {
    setRefreshFilter((prev) => !prev);
    setModalCompanyVisible(false);
  };

  const onAddedBranch = () => {
    setRefreshFilter((prev) => !prev);
    setModalBranchVisible(false);
  };

  const onAddedDevice = () => {
    fetchDevices();
    setModalDeviceVisible(false);
  };

  async function fetchAlerts() {
    let filterObj = {};
    try {
      if (!!searchBy.branch.id) {
        filterObj = {
          branchID: { eq: searchBy.branch.id },
        };
      } else if (!!searchBy.company.id || !!me.companyID) {
        filterObj = {
          companyID: { eq: searchBy.company.id || me.companyID },
        };
      } else {
        if (me.companyID)
          filterObj = {
            ...filterObj,
            or: [{ companyID: { eq: me.companyID } }],
          };
        if (me.managedCompanies.length > 0)
          filterObj = {
            ...filterObj,
            or: [
              ...(filterObj?.or ?? []),
              ...me.managedCompanies.map((cp) => ({ companyID: { eq: cp } })),
            ],
          };
      }

      const alertsData = await API.graphql(
        graphqlOperation(alertLogsByStatus, {
          status: "1",
          filter: {
            ...filterObj,
          },
        })
      );
      const alertsItems = alertsData.data.alertLogsByStatus.items ?? [];
      setAlerts(
        alertsItems.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
      );
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  async function fetchDevices() {
    try {
      setSelectedDevice();
      setDeviceVariables({
        loading: false,
        data: [],
      });
      setDevicesLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      let objOperation = {
        companyID: searchBy.company.id || me.companyID,
      };
      let nameOperation = devicesByCompanyID;
      let nameOperationStr = "devicesByCompanyID";

      if (!!searchBy.branch.id) {
        objOperation = {
          branchID: searchBy.branch.id,
        };
        nameOperation = devicesByBranchID;
        nameOperationStr = "devicesByBranchID";
      }

      const devicesData = await API.graphql(
        graphqlOperation(nameOperation, {
          ...objOperation,
        })
      );
      const devicesItems = devicesData.data[nameOperationStr].items;
      setDevices(await Promise.all(devicesItems));
      setDevicesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setDevicesLoading(false);
    } finally {
      setDevicesLoading(false);
    }
  }

  async function fetchDeviceVariables(deviceID) {
    try {
      setDeviceVariables({
        loading: true,
        data: [],
      });
      const deviceVariablesData = await API.graphql(
        graphqlOperation(deviceVariablesByDeviceID, {
          deviceID,
        })
      );
      const deviceVariablesItems =
        deviceVariablesData.data.deviceVariablesByDeviceID.items ?? [];
      setDeviceVariables({
        loading: false,
        data: deviceVariablesItems.map((devVar) => ({
          ...configInitialValues,
          deviceId: devVar.deviceID,
          variableId: devVar.variableID,
        })),
      });
    } catch (err) {
      toast.error(strings.responses.errorFetchDeviceVariables);
    }
  }

  useEffect(() => {
    fetchDevices();
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.branch.id, searchBy.company.id]);

  useEffect(() => {
    if (selectedDevice) {
      fetchDeviceVariables(selectedDevice.id);
    }
  }, [selectedDevice]);

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <div className="p-3">
        <Grid
          templateColumns={{ medium: "1fr", xl: "1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <Grid
            className="mb-3"
            templateColumns={{ medium: "1fr", xl: "1fr 1fr" }}
            rowGap="15px"
            columnGap="15px"
          >
            <CompanyBranchFilter
              searchBy={searchBy}
              setSearchBy={setSearchBy}
              refresh={refreshFilter}
            />
          </Grid>
          <Grid
            templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
            rowGap="15px"
            columnGap="15px"
          >
            {canCreateCompany && (
              <Button
                style={styles.buttonAdd}
                children={strings.actions.addCompany}
                variation="primary"
                onClick={() => setModalCompanyVisible(true)}
              />
            )}
            {canCreateBranch && (
              <Button
                style={styles.buttonAdd}
                children={strings.actions.addBranch}
                variation="primary"
                disabled={!searchBy.company.id}
                onClick={() => setModalBranchVisible(true)}
              />
            )}
            {canCreateDevice && (
              <Button
                style={styles.buttonAdd}
                children={strings.actions.addDevice}
                variation="primary"
                onClick={() => setModalDeviceVisible(true)}
              />
            )}
          </Grid>
        </Grid>
        {devicesLoading ? (
          <Loader />
        ) : (
          <Grid
            templateColumns={{ medium: "1fr", xl: "1fr 1fr" }}
            rowGap="15px"
            columnGap="15px"
          >
            <MonitorMap
              devices={devicesMapped}
              selectedDevice={selectedDevice}
              onSelectDevice={setSelectedDevice}
            />
            <div style={styles.containerTable}>
              <CTable
                className="table-centered"
                tableHeadProps={{ color: "light" }}
                responsive
                hover
                bordered
              >
                <CTableHead color="light">
                  <CTableRow>
                    <CTableHeaderCell
                      style={{ textAlign: "center" }}
                      colSpan={4}
                      scope="col"
                    >
                      {strings.table.headers.devices}
                    </CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableHead color="light">
                  <CTableRow>
                    <CTableHeaderCell scope="col">
                      {strings.table.headers.name}
                    </CTableHeaderCell>
                    <CTableHeaderCell scope="col">
                      {strings.table.headers.description}
                    </CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {devicesMapped?.map((item) => (
                    <CTableRow
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedDevice(item)}
                      active={selectedDevice?.id === item.id}
                    >
                      <CTableDataCell>
                        {item.name}{" "}
                        {item.alerts?.map((alertItem) => (
                          <AlertTreeIcon item={alertItem} />
                        ))}
                      </CTableDataCell>
                      <CTableDataCell>{item.description}</CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>
          </Grid>
        )}
        <Grid className="pt-5" rowGap="30px">
          {deviceVariables.loading ? (
            <Loader />
          ) : (
            deviceVariables.data.length > 0 && (
              <>
                <ChartFiltersForm
                  onSubmit={onUpdateCharts}
                  isAutoRefreshAll={isAutoRefreshAll}
                  onAutoRefreshAll={onAutoRefreshAll}
                />
                <Flex
                  wrap="wrap"
                  rowGap="30px"
                  columnGap="30px"
                  alignItems="flex-start"
                >
                  {deviceVariables.data?.map((item) => (
                    <DeviceChart deviceModel={item} />
                  ))}
                </Flex>
              </>
            )
          )}
        </Grid>
      </div>
      <CModal
        size="xl"
        alignment="center"
        visible={modalCompanyVisible}
        onClose={() => setModalCompanyVisible(false)}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>{strings.modalCompany.title}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CompanyCreateForm onSuccess={onAddedCompany} />
        </CModalBody>
      </CModal>
      <CModal
        size="xl"
        alignment="center"
        visible={modalBranchVisible}
        onClose={() => setModalBranchVisible(false)}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>{strings.modalBranch.title}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <BranchCreateForm
            onSuccess={onAddedBranch}
            baseCompanyId={searchBy.company.id}
          />
        </CModalBody>
      </CModal>
      <CModal
        size="xl"
        alignment="center"
        visible={modalDeviceVisible}
        onClose={() => setModalDeviceVisible(false)}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>{strings.modalDevice.title}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          <ScrollView maxHeight="75vh">
            <DeviceCreateForm onSuccess={onAddedDevice} />
          </ScrollView>
        </CModalBody>
      </CModal>
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    add: "Agregar",
    title: "Central de Monitoreo",
    modalCompany: {
      title: "Agregar Empresa",
    },
    modalBranch: {
      title: "Agregar Sede",
    },
    modalDevice: {
      title: "Agregar Dispositivo",
    },
    modalDeviceConfig: {
      deviceId: "ID del dispositivo seleccionado",
      deviceName: "Nombre del dispositivo seleccionado",
    },
    configCard: "Configuración de Tarjeta",
    actions: {
      addCompany: "Agregar Empresa",
      addBranch: "Agregar Sede",
      addDevice: "Agregar Dispositivo",
    },
    responses: {
      errorFetch: "Error al consultar dispositivos",
      errorFetchCompanies: "Error al consultar empresas",
      errorFetchBranches: "Error al consultar sedes",
    },
    fields: {
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      branch: {
        label: "Sede",
        placeholder: "Buscar sede...",
      },
    },
    table: {
      headers: {
        id: "ID",
        name: "Nombre",
        description: "Descripción",
        devices: "Dispositivos",
      },
    },
  },
});

const styles = {
  buttonAdd: { float: "right", marginBottom: "15px" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
  addBox: {
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  addIcon: {
    fontSize: "30px",
  },
  containerTable: {
    maxHeight: "400px",
    display: "inline-grid",
  },
};

export default Monitor;
